import { swabGrowthPositive, trimSwabField } from './swabResultUtils';
import { swabRowYYYYMM } from './efficacyMonthUtils';
import { EFFICACY_TERMS, postUvcPositiveKpiSubtext } from './efficacyTerminology';

const MAX_PATHOGEN_CELLS = 9;

export function daySortKey(iso) {
  if (!iso) return '';
  const s = String(iso).trim();
  const ymd = s.length >= 10 ? s.slice(0, 10) : s;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  return ymd;
}

/** Monday-based week bucket (local calendar). */
export function weekBucketFromIso(iso) {
  const sk = daySortKey(iso);
  if (!sk) return null;
  const d = new Date(`${sk}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diffToMonday);
  const sortKey = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
  return { sortKey, monday };
}

function isoWeekNumber(d) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
  );
}

function formatSpecimenLabel(raw) {
  const s = trimSwabField(raw);
  if (!s) return 'Surface';
  const spaced = s.replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function pathogenKeyFromRow(t) {
  const cat = trimSwabField(t.category);
  if (cat) return cat;
  return null;
}

function isPathogenCategory(name) {
  if (!name) return false;
  const up = name.toUpperCase().replace(/\s+/g, '_');
  const surfaceTokens = new Set([
    'TABLE',
    'PATIENT_BED',
    'BED',
    'PILLOW',
    'DOOR',
    'HANDRAIL',
    'OTHER',
    'UNSPECIFIED',
  ]);
  if (surfaceTokens.has(up)) return false;
  return true;
}

function eventSortKey(t) {
  return `${t.dateISO || ''}__${t.swabId || ''}__${t.location || ''}__${t.category || ''}`;
}

function isCleanEvent(t) {
  return !swabGrowthPositive(t.after ?? t.bacterialPost);
}

function isResidualEvent(t) {
  return swabGrowthPositive(t.after ?? t.bacterialPost);
}

/** Longest trailing streak of clean events (most recent run). */
function trailingCleanStreak(sortedAsc) {
  let streak = 0;
  for (let i = sortedAsc.length - 1; i >= 0; i -= 1) {
    if (isCleanEvent(sortedAsc[i])) streak += 1;
    else break;
  }
  return streak;
}

function findLastResidual(allTests) {
  const residuals = (Array.isArray(allTests) ? allTests : [])
    .filter(isResidualEvent)
    .filter((t) => daySortKey(t.dateISO))
    .sort((a, b) => String(b.dateISO).localeCompare(String(a.dateISO)));
  if (!residuals.length) return null;
  const t = residuals[0];
  const room = trimSwabField(t.room) || trimSwabField(t.location) || 'Location not set';
  const dateLabel =
    t.date ||
    (t.dateISO
      ? new Date(`${daySortKey(t.dateISO)}T12:00:00`).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
        })
      : '—');
  return { room, dateLabel, dateISO: t.dateISO };
}

function weeksOverlappingMonth(yyyyMm) {
  const [y, m] = yyyyMm.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return [];
  const monthStart = new Date(y, m - 1, 1);
  const monthEnd = new Date(y, m, 0);
  const weeks = new Map();
  for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const bucket = weekBucketFromIso(iso);
    if (!bucket) continue;
    if (!weeks.has(bucket.sortKey)) {
      weeks.set(bucket.sortKey, { sortKey: bucket.sortKey, monday: bucket.monday });
    }
  }
  return [...weeks.values()].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
}

/**
 * @param {object[]} monthSwabTests
 * @param {string} selectedMonth YYYY-MM
 */
export function buildTimelineWeeks(monthSwabTests, selectedMonth) {
  const weekDefs = weeksOverlappingMonth(selectedMonth);
  const eventsByWeek = new Map();
  for (const t of monthSwabTests) {
    const bucket = weekBucketFromIso(t.dateISO);
    if (!bucket) continue;
    if (!eventsByWeek.has(bucket.sortKey)) eventsByWeek.set(bucket.sortKey, []);
    eventsByWeek.get(bucket.sortKey).push({
      id: eventSortKey(t),
      status: isResidualEvent(t) ? 'residual' : 'clean',
      dateISO: t.dateISO,
      location: trimSwabField(t.location) || '—',
      specimenLabel: formatSpecimenLabel(t.specimen),
      category: trimSwabField(t.category) || '—',
      before: trimSwabField(t.before) || '—',
      after: trimSwabField(t.after) || '—',
      dayKey: daySortKey(t.dateISO),
      row: t,
    });
  }

  return weekDefs.map((w) => {
    const events = (eventsByWeek.get(w.sortKey) || []).sort((a, b) =>
      String(a.dateISO).localeCompare(String(b.dateISO)),
    );
    return {
      sortKey: w.sortKey,
      weekLabel: `Week ${isoWeekNumber(w.monday)}`,
      events,
      empty: events.length === 0,
    };
  });
}

/**
 * @param {object[]} monthSwabTests
 * @returns {{ name: string, status: 'cleared'|'warning'|'screened', detail: string, detailRows?: object[] }[]}
 */
export function buildPathogenShieldCells(monthSwabTests) {
  const stats = new Map();

  for (const t of monthSwabTests) {
    const name = pathogenKeyFromRow(t);
    if (!name || !isPathogenCategory(name)) continue;
    if (!stats.has(name)) {
      stats.set(name, { beforePos: 0, afterPos: 0, cleared: 0, n: 0, reportIds: new Set(), detailRows: [] });
    }
    const g = stats.get(name);
    g.n += 1;
    if (t.swabId) g.reportIds.add(String(t.swabId));
    const bPos = swabGrowthPositive(t.before);
    const aPos = swabGrowthPositive(t.after);
    // Show detail rows only when at least one CFU value is reported (before/after positive).
    if (bPos || aPos) {
      g.detailRows.push({
        cfuIdentified: t.before != null && String(t.before).trim() !== '' ? String(t.before).trim() : '—',
        cfuPostUvc: t.after != null && String(t.after).trim() !== '' ? String(t.after).trim() : '—',
        date: t.date || '—',
        highlightedLocation: formatSpecimenLabel(t.specimen),
        room: t.room || '—',
        location: t.location || '—',
        dateISO: t.dateISO || '',
      });
    }
    if (bPos) g.beforePos += 1;
    if (aPos) g.afterPos += 1;
    if (bPos && !aPos) g.cleared += 1;
  }

  const cells = [];
  for (const [name, g] of stats) {
    let status = 'screened';
    let detail = '';
    if (g.afterPos > 0) {
      status = 'warning';
      detail = EFFICACY_TERMS.postUvcPositive.pathogenDetail(g.afterPos);
    } else if (g.beforePos > 0 && g.cleared === g.beforePos) {
      status = 'cleared';
      detail = `${g.cleared}/${g.beforePos} cleared`;
    } else if (g.beforePos > 0) {
      status = 'cleared';
      detail = `${g.cleared}/${g.beforePos} cleared`;
    } else if (g.n > 0) {
      status = 'screened';
      const nReports = g.reportIds.size || 1;
      detail = `screened in ${nReports} report${nReports === 1 ? '' : 's'}, none identified positive`;
    } else {
      detail = 'no growth detected';
      status = 'cleared';
    }
    const detailRows = [...g.detailRows].sort((a, b) =>
      String(b.dateISO || '').localeCompare(String(a.dateISO || '')),
    );
    cells.push({
      name,
      status,
      detail,
      detailRows,
      sortWeight: status === 'warning' ? 0 : status === 'cleared' ? 1 : 2,
    });
  }

  cells.sort((a, b) => a.sortWeight - b.sortWeight || a.name.localeCompare(b.name));

  if (cells.length <= MAX_PATHOGEN_CELLS) return cells;

  const primary = cells.slice(0, MAX_PATHOGEN_CELLS - 1);
  const rest = cells.slice(MAX_PATHOGEN_CELLS - 1);
  const allCleared = rest.every((c) => c.status === 'cleared' || c.status === 'screened');
  primary.push({
    name: 'All others',
    status: allCleared ? 'cleared' : 'screened',
    detail: allCleared ? 'no growth detected' : `${rest.length} more on panel`,
    detailRows: [],
    sortWeight: 2,
  });
  return primary;
}

/**
 * @param {object[]} monthSwabTests
 * @param {object[]} allSwabTests for last-residual banner
 * @param {object[]} swabReports
 * @param {string} selectedMonth
 * @param {{ residualCount: number, allResolved: boolean }} residualMeta
 */
export function buildProtectionMetrics(monthSwabTests, allSwabTests, swabReports, selectedMonth, residualMeta) {
  const monthTests = Array.isArray(monthSwabTests) ? monthSwabTests : [];
  const sortedAsc = [...monthTests]
    .filter((t) => daySortKey(t.dateISO))
    .sort((a, b) => String(a.dateISO).localeCompare(String(b.dateISO)) || eventSortKey(a).localeCompare(eventSortKey(b)));

  const cleanEvents = monthTests.filter(isCleanEvent).length;
  const residualEvents = monthTests.filter(isResidualEvent).length;
  const reportIds = new Set(monthTests.map((t) => (t.swabId ? String(t.swabId) : null)).filter(Boolean));
  const monthReports = (Array.isArray(swabReports) ? swabReports : []).filter(
    (r) => swabRowYYYYMM(r) === selectedMonth,
  );
  const reportCount = monthReports.length || reportIds.size;

  const pathogenNames = new Set();
  for (const t of monthTests) {
    const name = pathogenKeyFromRow(t);
    if (name && isPathogenCategory(name)) pathogenNames.add(name);
  }

  const timelineWeeks = buildTimelineWeeks(monthTests, selectedMonth);
  const pathogenCells = buildPathogenShieldCells(monthTests);
  const streak = trailingCleanStreak(sortedAsc);
  const lastResidual = findLastResidual(allSwabTests);

  let cumulative = 0;
  const cumulativeByDate = [];
  const byDate = new Map();
  for (const t of sortedAsc) {
    if (!isCleanEvent(t)) continue;
    const dk = daySortKey(t.dateISO);
    byDate.set(dk, (byDate.get(dk) || 0) + 1);
  }
  for (const dk of [...byDate.keys()].sort()) {
    cumulative += byDate.get(dk);
    cumulativeByDate.push({ date: dk, cumulative });
  }

  return {
    protectedSurfaceDays: cleanEvents,
    cleanSwabEvents: cleanEvents,
    reportCount,
    pathogenCount: pathogenNames.size,
    residualCount: residualMeta.residualCount ?? residualEvents,
    residualsSubtitle: postUvcPositiveKpiSubtext(
      residualMeta.residualCount ?? residualEvents,
      residualMeta.allResolved,
    ),
    streak,
    lastResidual,
    timelineWeeks,
    pathogenCells,
    cumulativeByDate,
    totalEvents: monthTests.length,
  };
}
